// Una tarjeta de referencia: Añadir producto, Añadir modelo o Añadir referencia. Nació
// adentro de `CajaDeRedes` (ADR-2026-10-01-modo-redes) y se extrajo tal cual para que la
// barra de Editar del visor use LAS MISMAS tarjetas
// (ADR-2026-10-06-editar-con-referencias-y-escritor). Sin `tema`, es byte a byte la de
// Redes; `tema: 'oscuro'` es la variante sobre el vidrio del visor.

import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Package, X } from 'lucide-react';
import type { ChatAttachment } from '../../agentAttachments';
import { miniaturaDeAdjunto } from '../../etiquetaDeAdjunto';
import { CarpetaDeImagenes } from './CarpetaDeImagenes';

/** Los colores de cada tema. `claro` es la tarjeta de Redes, sin cambios. */
const TEMAS = {
  claro: {
    boton: 'rounded-xl bg-[#F3F4F8]',
    icono: 'bg-white',
    iconoColor: 'text-brand-primary',
    titulo: 'text-brand-black',
    subtitulo: 'text-[#02062B]/[0.45]',
    chevron: 'text-brand-black/30',
    quitar: 'bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.1] hover:text-brand-black',
    anillo: 'ring-white',
    fondoMiniatura: 'bg-neutral-100',
  },
  // Sobre el vidrio oscuro del visor. Radio 20: la pastilla es de 28 y las tarjetas van a
  // 8 px del borde (regla concéntrica).
  oscuro: {
    boton: 'rounded-[20px] bg-white/[0.08] hover:bg-white/[0.12]',
    icono: 'bg-white/10',
    iconoColor: 'text-white',
    titulo: 'text-white',
    subtitulo: 'text-white/50',
    chevron: 'text-white/40',
    quitar: 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white',
    anillo: 'ring-black/40',
    fondoMiniatura: 'bg-white/10',
  },
} as const;

/**
 * Una tarjeta de referencia, adentro de la caja y arriba del prompt: Añadir producto, Añadir
 * modelo o Añadir referencia. Es la
 * card de "elegir el producto" de los modos en el teléfono (`PasoUnoMobile`): baja (72 px),
 * horizontal, con el ícono a la izquierda —o el ABANICO de miniaturas de lo elegido—, el
 * texto en el medio y la flecha a la derecha. Con algo elegido, la flecha se vuelve la X que lo
 * quita; tocar la tarjeta siempre abre el selector del Agente en su pestaña.
 */
export const TarjetaDeReferencia: React.FC<{
  rotulo: string;
  Icono: typeof Package;
  /** Lo elegido de esta tarjeta (uno, o varias imágenes subidas). */
  adjuntos: ChatAttachment[];
  onAbrir: () => void;
  onQuitar: () => void;
  /**
   * La carpetita con fotos en vez del ícono, mientras NO hay nada elegido: asoman las
   * últimas que tiene la cuenta (o el `relleno`) y al pasar el mouse por la tarjeta se
   * abren en abanico. Con algo elegido la carpeta se va y quedan las miniaturas de eso.
   */
  carpeta?: { imagenes: string[]; tono: 'azul' | 'violeta' | 'gris'; relleno?: React.ReactNode };
  /** Marca la tarjeta como destino de la animación de "Editar" (`data-destino-referencia`). */
  esDestino?: boolean;
  /** Un texto que pisa al de siempre mientras pasa algo ("Subiendo…"). */
  estado?: string;
  /** `claro` (Redes, default) u `oscuro` (la barra del visor). */
  tema?: keyof typeof TEMAS;
}> = ({ rotulo, Icono, adjuntos, onAbrir, onQuitar, carpeta, esDestino = false, estado, tema = 'claro' }) => {
  const t = TEMAS[tema];
  const [hover, setHover] = useState(false);
  // Cada vez que se SUMA algo (un id que antes no estaba), la miniatura y el texto rebotan
  // (ADR-2026-10-04-rebote-al-anadir-un-adjunto). El contador va de `key`, así la animación
  // vuelve a correr aunque la tarjeta ya tuviera algo; quitar no la dispara, y lo que ya
  // estaba al montar tampoco.
  const [pulso, setPulso] = useState(0);
  const idsPrevios = useRef<string[] | null>(null);
  useEffect(() => {
    const ids = adjuntos.map((a) => a.id);
    const previos = idsPrevios.current;
    idsPrevios.current = ids;
    if (previos && ids.some((id) => !previos.includes(id))) setPulso((p) => p + 1);
  }, [adjuntos]);
  const rebote = pulso > 0 ? 'animate-rebote-de-adjunto' : '';
  const reboteDeTexto = pulso > 0 ? 'animate-rebote-de-texto' : '';
  // El abanico es sólo de lo ELEGIDO; vacía, la tarjeta lleva el ícono (o la carpeta).
  const elegidas = adjuntos.map((a) => miniaturaDeAdjunto(a)).filter((u): u is string => !!u);
  const abanico = elegidas.slice(0, 3);
  // La carpeta sólo mientras NO hay nada elegido; con algo, el abanico de sus miniaturas.
  const fotosDeCarpeta = carpeta && adjuntos.length === 0 ? carpeta.imagenes.slice(0, 3) : [];
  // Qué dice la tarjeta: su rótulo vacía; con una cosa, cuál; con varias, cuántas. Las
  // referencias pueden mezclar imágenes y un video.
  const esMedia = (a: ChatAttachment) => a.kind === 'image' || a.kind === 'video';
  const unidad = rotulo.replace(/^Añadir /, '').toLowerCase();
  const texto = estado
    ? estado
    : adjuntos.length === 0
      ? rotulo
      : adjuntos.length === 1
        ? adjuntos[0].kind === 'image'
          ? '1 imagen'
          : adjuntos[0].kind === 'video'
            ? '1 video'
            : adjuntos[0].name
        : adjuntos.every((a) => a.kind === 'image')
          ? `${adjuntos.length} imágenes`
          : adjuntos.every(esMedia)
            ? `${adjuntos.length} referencias`
            : `${adjuntos.length} ${unidad}s`;
  return (
    <div className="group relative min-w-0 flex-1" {...(esDestino ? { 'data-destino-referencia': '' } : {})}>
      <button
        type="button"
        onClick={onAbrir}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={`flex h-[64px] w-full items-center gap-3 ${t.boton} px-3 pr-3.5 text-left transform transition-transform duration-300 hover:-translate-y-0.5`}
      >
        {carpeta && adjuntos.length === 0 && (fotosDeCarpeta.length > 0 || carpeta.relleno) ? (
          <span className="grid h-[42px] w-[42px] shrink-0 place-items-center">
            <CarpetaDeImagenes imagenes={fotosDeCarpeta} relleno={carpeta.relleno} tono={carpeta.tono} abierta={hover} ancho={36} alto={27} />
          </span>
        ) : abanico.length > 0 ? (
          // Las miniaturas se pisan un poco entre sí (-ml) para leerse como un grupo.
          <span key={pulso} className={`inline-flex shrink-0 items-center ${rebote}`}>
            {abanico.map((url, i) => (
              <span key={`${url}:${i}`} className={`block h-[42px] w-[42px] overflow-hidden rounded-lg ${t.fondoMiniatura} ring-2 ${t.anillo} ${i > 0 ? '-ml-3' : ''}`}>
                <img src={url} alt="" className="h-full w-full object-cover" />
              </span>
            ))}
          </span>
        ) : (
          <span className={`grid h-[42px] w-[42px] shrink-0 place-items-center rounded-lg ${t.icono}`}>
            <Icono className={`h-5 w-5 ${t.iconoColor}`} strokeWidth={2} />
          </span>
        )}
        <span key={`t${pulso}`} className={`min-w-0 flex-1 ${reboteDeTexto}`}>
          <span className={`block truncate text-[13.5px] font-semibold ${t.titulo}`}>{texto}</span>
          {adjuntos.length > 0 && <span className={`block text-[12px] ${t.subtitulo}`}>{rotulo.replace(/^Añadir /, '')}</span>}
        </span>
        {adjuntos.length === 0 && <ChevronRight className={`h-4 w-4 shrink-0 ${t.chevron}`} />}
      </button>
      {adjuntos.length > 0 && (
        <button
          type="button"
          onClick={onQuitar}
          aria-label={`Quitar ${rotulo.toLowerCase()}`}
          className={`absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full ${t.quitar} transition-colors`}
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
};

export default TarjetaDeReferencia;
